import React, { useEffect, useState } from 'react';
import { AlertTriangle, Building2, Link2, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminUserService,
  AdminUserListItem,
  CorrispondenzaGestionale,
  SceltaApprovazione,
} from '../../services/adminUserService';
import { getErrorMessage } from '../../utils/errorMessage';

/**
 * Dialogo di Approvazione di una Registrazione. Cerca sul Gestionale i clienti
 * che hanno già la stessa Partita IVA (`.scratch/partita-iva-unica/spec.md`):
 * se ce ne sono propone **Collega** (scelta predefinita, vietata su un conto
 * che ha già un Titolare) o **Approva come Contatto** (es. una sede
 * distinta). Senza corrispondenze è la conferma di sempre.
 *
 * Senza Collega l'approvazione attiva l'account come **Contatto**, senza
 * creare il cliente sul Gestionale: il cliente nasce al suo primo ordine
 * (`.scratch/contatti-lead/spec.md`).
 */

type Scelta = number | 'crea' | null;

export function ApprovaRegistrazioneDialog({
  user, onClose, onApproved,
}: { user: AdminUserListItem | null; onClose: () => void; onApproved: (user: AdminUserListItem) => void }) {
  const [corrispondenze, setCorrispondenze] = useState<CorrispondenzaGestionale[] | null>(null);
  const [erroreRicerca, setErroreRicerca] = useState(false);
  const [scelta, setScelta] = useState<Scelta>(null);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const mostra = (lista: CorrispondenzaGestionale[]) => {
    setCorrispondenze(lista);
    const libera = lista.find((c) => !c.titolare);
    setScelta(lista.length === 0 ? 'crea' : libera ? libera.an_conto : null);
  };

  useEffect(() => {
    if (!user) return;
    setCorrispondenze(null);
    setErroreRicerca(false);
    setScelta(null);
    setErrore(null);
    adminUserService
      .corrispondenze(user.userId)
      .then(mostra)
      .catch(() => { setErroreRicerca(true); setCorrispondenze([]); setScelta('crea'); });
  }, [user]);

  if (!user) return null;
  const nome = user.ragioneSociale || user.email;
  const tutteConTitolare = !!corrispondenze?.length && corrispondenze.every((c) => c.titolare);

  const conferma = async () => {
    if (scelta === null) return;
    const body: SceltaApprovazione | undefined =
      typeof scelta === 'number' ? { collegaConto: scelta } : corrispondenze?.length ? { creaComunque: true } : undefined;
    setSaving(true);
    setErrore(null);
    try {
      await adminUserService.approve(user.userId, body);
      toast.success(typeof scelta === 'number'
        ? `${nome} approvato e collegato al cliente ${scelta}.`
        : `${nome} approvato come Contatto: il cliente sul gestionale nascerà al primo ordine.`);
      onApproved(user);
    } catch (err: any) {
      // Il Gestionale ha la Partita IVA ma la ricerca non l'aveva trovata
      // (o è cambiato qualcosa nel frattempo): si mostra la scelta.
      const nuove = err?.response?.data?.detail?.corrispondenze;
      if (Array.isArray(nuove)) {
        mostra(nuove);
        toast.error(err.response.data.detail.message);
      } else {
        setErrore(getErrorMessage(err, "Errore durante l'approvazione."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-lg mx-4" data-testid="approva-registrazione-dialog">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-100">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-base">Approva Registrazione</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Confermi l'approvazione di {nome}? L'utente potrà accedere all'area clienti e riceverà un'email di
              conferma. L'azione verrà registrata nello storico con la tua email.
            </p>
            <p className="text-sm text-muted-foreground mt-2" data-testid="approva-nota-contatto">
              Senza collegarlo a un cliente esistente, l'account viene attivato come <strong>Contatto</strong>: vede
              catalogo e prezzi e può ordinare. Il cliente sul gestionale viene creato al suo primo ordine.
            </p>
          </div>
        </div>

        {corrispondenze === null ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Cerco la Partita IVA nel gestionale…
          </p>
        ) : corrispondenze.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-4" data-testid="approva-nessuna-corrispondenza">
            {erroreRicerca
              ? 'Non è stato possibile cercare la Partita IVA nel gestionale: se c\'è già un cliente, te lo diremo alla conferma.'
              : `Nessun cliente del gestionale ha la Partita IVA ${user.partitaIva || ''}: l'account sarà un Contatto, e il cliente verrà creato al primo ordine.`}
          </p>
        ) : (
          <div className="mb-4" data-testid="approva-corrispondenze">
            <p className="text-sm mb-2">
              Il gestionale ha già {corrispondenze.length === 1 ? 'un cliente' : `${corrispondenze.length} clienti`} con
              la Partita IVA <span className="font-medium">{user.partitaIva}</span>.
            </p>
            {tutteConTitolare && (
              <p className="text-xs rounded-lg bg-red-50 text-red-700 p-2 mb-2" data-testid="approva-tutti-con-titolare">
                Ogni cliente con questa Partita IVA ha già un titolare: respingi la registrazione. I colleghi vanno
                aggiunti come operatori dal titolare.
              </p>
            )}
            <div className="space-y-2">
              {corrispondenze.map((c) => (
                <label
                  key={c.an_conto}
                  className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${c.titolare ? 'opacity-60' : 'cursor-pointer hover:bg-secondary/40'} ${scelta === c.an_conto ? 'border-[hsl(214,58%,41%)] bg-blue-50/40' : ''}`}
                  data-testid={`approva-collega-${c.an_conto}`}
                >
                  <input
                    type="radio"
                    name="scelta-approvazione"
                    className="mt-1"
                    disabled={!!c.titolare}
                    checked={scelta === c.an_conto}
                    onChange={() => setScelta(c.an_conto)}
                  />
                  <span>
                    <span className="flex items-center gap-1.5 font-medium">
                      <Link2 className="w-3.5 h-3.5" /> Collega a {c.an_conto} · {c.ragioneSociale || '—'}
                    </span>
                    <span className="block text-xs text-muted-foreground">{c.citta || '—'}</span>
                    {c.titolare && (
                      <span className="block text-xs text-red-700" data-testid={`approva-titolare-${c.an_conto}`}>
                        Ha già un titolare: {c.titolare.email}
                      </span>
                    )}
                  </span>
                </label>
              ))}
              <label
                className={`flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer hover:bg-secondary/40 ${scelta === 'crea' ? 'border-[hsl(214,58%,41%)] bg-blue-50/40' : ''}`}
                data-testid="approva-crea-comunque"
              >
                <input
                  type="radio"
                  name="scelta-approvazione"
                  className="mt-1"
                  checked={scelta === 'crea'}
                  onChange={() => setScelta('crea')}
                />
                <span>
                  <span className="flex items-center gap-1.5 font-medium"><Plus className="w-3.5 h-3.5" /> Approva come Contatto, senza collegare</span>
                  <span className="block text-xs text-muted-foreground">
                    Per esempio una sede distinta della stessa azienda: un nuovo cliente verrà creato al primo ordine.
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}

        {errore && (
          <p className="flex items-start gap-2 text-sm rounded-lg bg-red-50 text-red-700 p-3 mb-4" role="alert" data-testid="approva-errore">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {errore}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg border text-sm font-medium hover:bg-secondary/60 transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={conferma}
            disabled={saving || scelta === null}
            className="h-9 px-4 rounded-lg text-sm font-medium text-white transition-colors bg-amber-500 hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="approva-conferma-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : typeof scelta === 'number' ? <Link2 className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
            {typeof scelta === 'number' ? 'Approva e collega' : 'Approva come Contatto'}
          </button>
        </div>
      </div>
    </div>
  );
}
